import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SERVICE_STATUS } from "./serviceMeta";
import { localToday } from "@/components/equipment/equipmentMeta";

const inputCls = "w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand";
const empty = () => ({ title: "", description: "", customer: "", address: "", scheduled_date: localToday(), scheduled_time: "", assigned_to: "", equipment_id: "", location: "", status: "scheduled" });

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-bold text-zinc-900 block mb-1">{label}</label>
    {children}
  </div>
);

export const ServiceJobDialog = ({ open, onOpenChange, editing, technicians, equipment, onSaved, defaultDate }) => {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  useEffect(() => {
    if (open) api.get("/my-locations").then((r) => setBranches(r.data)).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (!open) return;
    if (editing) setForm(Object.fromEntries(Object.keys(empty()).map((k) => [k, editing[k] ?? ""])));
    else setForm({ ...empty(), scheduled_date: defaultDate || localToday() });
  }, [open, editing, defaultDate]);
  const bind = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/service-jobs/${editing.id}`, form);
      else await api.post("/service-jobs", form);
      toast.success(editing ? "Service job updated" : "Service job created");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="service-job-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tighter">{editing ? "Edit Service Job" : "New Service Job"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Title"><input required maxLength={200} {...bind("title")} data-testid="service-title-input" className={inputCls} placeholder="e.g. 6-monthly crane service" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Scheduled date"><input type="date" required {...bind("scheduled_date")} data-testid="service-date-input" className={inputCls} /></Field>
            <Field label="Time (optional)"><input type="time" {...bind("scheduled_time")} data-testid="service-time-input" className={inputCls} /></Field>
            <Field label="Customer"><input maxLength={200} {...bind("customer")} data-testid="service-customer-input" className={inputCls} /></Field>
            <Field label="Service Technician">
              <select {...bind("assigned_to")} data-testid="service-tech-select" className={inputCls}>
                <option value="">Unassigned</option>
                {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Site address"><textarea rows={2} maxLength={500} {...bind("address")} data-testid="service-address-input" className={inputCls} /></Field>
          <Field label="Branch (optional)">
            <select {...bind("location")} data-testid="service-location-select" className={inputCls}>
              <option value="">No branch (visible to all)</option>
              {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Equipment (optional)">
              <select {...bind("equipment_id")} data-testid="service-equipment-select" className={inputCls}>
                <option value="">None</option>
                {equipment.map((q) => <option key={q.id} value={q.id}>{q.asset_id ? `${q.asset_id} — ` : ""}{q.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select {...bind("status")} data-testid="service-status-select" className={inputCls}>
                {Object.entries(SERVICE_STATUS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Work description"><textarea rows={3} maxLength={4000} {...bind("description")} data-testid="service-description-input" className={inputCls} /></Field>
          {technicians.length === 0 && <p className="text-xs text-amber-700">No Service Technicians yet — an admin can add one on the Users page.</p>}
          <DialogFooter>
            <button type="submit" disabled={saving} data-testid="service-submit-button"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Create service job"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
